// storybook-check-ignore
// component-test-check-ignore
import React from 'react';

import { LocalDate } from '@js-joda/core';
import { Box, Flex, Image, List, ListItem, Text } from '@opendoor/bricks/core';

import {
  PRIVACY_CENTER_URL,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
} from 'shared/helpers/routes';

import { LayoutContent } from 'platform/components/layout/Layout';
import { LegacyExternalLink, LegacyExternalLinkProps } from 'platform/remix-run/react';

const Separator: React.FC = () => <Box height="12px" width="1px" bgColor="neutrals90" mx={2} />;

const Trademark: React.FC = () => (
  <Flex alignItems="center">
    <Text size="s00" color="neutrals90" data-testid="trademark">
      &copy; {LocalDate.now().year()} Opendoor
    </Text>
    <Separator />
    <Text size="s00" color="neutrals90">
      All Rights Reserved
    </Text>
  </Flex>
);

interface FooterLinkProps extends Omit<LegacyExternalLinkProps, 'aria-label'> {
  displayName: string;
}

const FooterLink: React.FC<FooterLinkProps> = ({ analyticsName, displayName, href, ...props }) => (
  <LegacyExternalLink
    analyticsName={analyticsName}
    href={href}
    target="_blank"
    textDecoration="underline"
    textDecorationColor="neutrals90"
    fontSize="s00"
    lineHeight="s00"
    color="neutrals90"
    aria-label={null}
    _hover={{
      color: 'neutrals90',
    }}
    {...props}
  >
    {displayName}
  </LegacyExternalLink>
);

const LegalLinks: React.FC<NovoFooterProps> = ({ closingOrClosedOffer = false }) => {
  return (
    <List variant="text" display="flex" flexWrap="wrap" alignItems="center" mb={5}>
      <ListItem>
        <FooterLink
          analyticsName="terms-of-service"
          displayName="Terms of Service"
          href={TERMS_OF_SERVICE_URL}
        />
      </ListItem>
      <ListItem>
        <Separator />
      </ListItem>
      {closingOrClosedOffer && (
        <>
          <ListItem>
            <FooterLink
              analyticsName="notice-at-collection"
              displayName="Notice at Collection"
              href={`${TERMS_OF_SERVICE_URL}/#ccpa`}
            />
          </ListItem>
          <ListItem>
            <Separator />
          </ListItem>
        </>
      )}

      <ListItem>
        <FooterLink
          analyticsName="privacy-policy"
          displayName="Privacy Policy"
          href={PRIVACY_POLICY_URL}
        />
      </ListItem>
      <ListItem>
        <Separator />
      </ListItem>
      <ListItem>
        <FooterLink
          analyticsName="do-not-sell-my-info"
          displayName="Do Not Sell My Info"
          href={PRIVACY_CENTER_URL}
        />
      </ListItem>
    </List>
  );
};

/**
 * The footer that appears on all pages.
 */
interface NovoFooterProps {
  closingOrClosedOffer?: boolean;
}
export const NovoFooter: React.FC<NovoFooterProps> = ({ closingOrClosedOffer }) => {
  return (
    <LayoutContent
      flexDir={['column', null, 'row']}
      data-testid="global-footer"
      width="100%"
      justifyContent="space-between"
    >
      <Box mb={5}>
        <Trademark />
        <LegalLinks closingOrClosedOffer={closingOrClosedOffer} />
      </Box>
      <Box>
        <Image
          src="https://imgdrop.imgix.net/d0677f541b294b4f9de00719940717b3.svg"
          alt="Equal housing opportunity"
          w="36px"
          h="35px"
          lineHeight="100"
        />
      </Box>
    </LayoutContent>
  );
};
